/**
 * 本地进度与设置。localStorage 用不了（隐私模式、被禁用）时静默降级为内存态，
 * 保证游戏本身照常能玩。
 */

const KEY_PROGRESS = 'sd_progress';
const KEY_SETTINGS = 'sd_settings';

/** localStorage 不可用时的兜底。 */
const memory = new Map();

function readRaw(key) {
    try {
        const v = localStorage.getItem(key);
        if (v !== null) return v;
    } catch { /* 忽略 */ }
    return memory.get(key) ?? null;
}

function writeRaw(key, value) {
    memory.set(key, value);
    try {
        localStorage.setItem(key, value);
    } catch { /* 忽略：配额满或被禁用 */ }
}

function readJson(key, fallback) {
    const raw = readRaw(key);
    if (raw === null) return fallback;
    try {
        return { ...fallback, ...JSON.parse(raw) };
    } catch {
        return fallback;
    }
}

// ── 进度 ──

const DEFAULT_PROGRESS = {
    /** 这份进度属于哪一天（本地日期键）。跨天自动作废重来。 */
    date: '',
    /** 当天已解开的题号（0..9，指当天题组内的序号） */
    solved: [],
};

/**
 * 读当天进度。存的不是今天的就返回空进度 —— 每日题组不跨天延续。
 * @param {string} today 本地日期键
 */
export function loadProgress(today) {
    const p = readJson(KEY_PROGRESS, DEFAULT_PROGRESS);
    if (p.date !== today || !Array.isArray(p.solved)) {
        return { date: today, solved: [] };
    }
    // 去重并只保留合法序号，防止手改 localStorage 把 UI 搞乱
    const solved = [...new Set(p.solved.filter(n => Number.isInteger(n) && n >= 0))];
    return { date: today, solved };
}

/**
 * 记一道已解开。
 * @param {string} today
 * @param {number} slot 当天题组内的序号
 */
export function markSolved(today, slot) {
    const p = loadProgress(today);
    if (!p.solved.includes(slot)) p.solved.push(slot);
    writeRaw(KEY_PROGRESS, JSON.stringify(p));
    return p;
}

// ── 新手引导 ──

const KEY_WELCOME = 'sd_welcome';

/**
 * 今天是否已经看过欢迎弹窗。存的是日期而不是布尔 —— 引导每天第一关重放一次，
 * 隔天自然过期，不需要额外的清理逻辑。
 * @param {string} today 本地日期键
 */
export function welcomeSeen(today) {
    return readRaw(KEY_WELCOME) === today;
}

/** @param {string} today */
export function markWelcomeSeen(today) {
    writeRaw(KEY_WELCOME, today);
}

// ── 设置 ──

const DEFAULT_SETTINGS = {
    sound: true,
    /** 'light' | 'dark' | '' （空串=跟随系统） */
    theme: '',
};

export function loadSettings() {
    const s = readJson(KEY_SETTINGS, DEFAULT_SETTINGS);
    s.sound = s.sound !== false;
    if (s.theme !== 'light' && s.theme !== 'dark') s.theme = '';
    return s;
}

/** @param {Partial<typeof DEFAULT_SETTINGS>} patch */
export function saveSettings(patch) {
    const next = { ...loadSettings(), ...patch };
    writeRaw(KEY_SETTINGS, JSON.stringify(next));
    return next;
}
